import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { http } from "@/lib/http";
import { Switch } from "@/components/ui/switch";
import { useLongPress } from "@/hooks/use-long-press";
import { FileContextMenu } from "@/components/ui/FileContextMenu";
import { cn } from "@/lib/utils";
import { RefreshCw, Files, Eye, EyeClosed, Search, GitCommit } from "lucide-react";
import { CoverageInfo, FileMeta } from "@/core/state-shape";
import { FileNode } from "../types";
import { formatTokenCount } from "../utils";
import { useWorkspaceStore } from "@/stores/workspace";
import { useFilesStore } from "@/stores/files";
import { useFileSelection } from "@/hooks/workspace/useFileSelection";
import { useWorkspaceActions } from "@/hooks/workspace/useWorkspaceActions";
import {
  useProcessedFileTree,
} from "@/hooks/workspace/useFileTree";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { SaveNewFileDialog } from "../SaveNewFileDialog";
import { useShallow } from 'zustand/react/shallow';
import { useUiStore } from '@/stores/ui';
import { WorkspaceSearch } from "./WorkspaceSearch";
import { GitPanel } from './GitPanel';

const TestStatusIndicator = ({
  testSummary,
}: {
  testSummary?: FileMeta["testSummary"];
}) => {
  if (!testSummary || testSummary.total === 0) {
    return null;
  }

  const isFailing = testSummary.failing > 0;
  const text = `${testSummary.passing}/${testSummary.total}`;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            "flex items-center gap-1 text-[10px] tabular-nums",
            isFailing ? "text-destructive" : "text-green-600"
          )}
        >
          <span>{isFailing ? "✖" : "✔"}</span>
          <span>{text}</span>
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <p>{testSummary.passing} Passing</p>
        {testSummary.failing > 0 && (
          <p className="text-destructive">{testSummary.failing} Failing</p>
        )}
        {(testSummary.flaky ?? 0) > 0 && (
          <p className="text-yellow-500">{testSummary.flaky} Flaky</p>
        )}
      </TooltipContent>
    </Tooltip>
  );
};

const CoverageIndicator = ({
  coverage,
  coverageDesynced,
}: {
  coverage?: CoverageInfo;
  coverageDesynced?: boolean;
}) => {
  if (!coverage || typeof coverage.summary?.lines?.pct !== "number") {
    return null;
  }

  const pct = coverage.summary.lines.pct;
  let colorClass = "bg-gray-400";
  if (pct >= 80) {
    colorClass = "bg-green-500";
  } else if (pct >= 50) {
    colorClass = "bg-yellow-500";
  } else if (pct > 0) {
    colorClass = "bg-red-500";
  } else {
    // For 0% coverage, show a muted bar instead of red
    colorClass = "bg-muted";
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex items-center gap-1">
          <div className="w-10 h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className={cn("h-full", colorClass)}
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="text-[10px] w-8 text-right tabular-nums">
            {pct.toFixed(0)}%{coverageDesynced ? "*" : ""}
          </span>
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <p>
          Line Coverage: {pct.toFixed(1)}% ({coverage.summary.lines.covered}/
          {coverage.summary.lines.total})
        </p>
        {coverageDesynced && (
          <p className="text-yellow-500">
            Note: Coverage data may be out of sync.
          </p>
        )}
        {coverage.summary.branches.pct && (
          <p>Branches: {coverage.summary.branches?.pct.toFixed(1)}%</p>
        )}
      </TooltipContent>
    </Tooltip>
  );
};

// Shared File Explorer Component
const _FileExplorer = ({
  onRefresh
}: {
  onRefresh: () => void
}) => {
  const {
    fileTree,
    userSelectedFiles,
    contextSelectedFiles,
    isAutoContextEnabled,
    expandedFolders,
    editedContent,
    openFilePath,
    setExpandedFolders,
    setIsAutoContextEnabled,
    openInPreview,
    openPermanently,
  } = useWorkspaceStore(
    useShallow(
    (state) => ({
    fileTree: state.fileTree,
    userSelectedFiles: state.userSelectedFiles,
    contextSelectedFiles: state.contextSelectedFiles,
    isAutoContextEnabled: state.isAutoContextEnabled,
    expandedFolders: state.expandedFolders,
    editedContent: state.editedContent,
    openFilePath: state.openFilePath,
    tokenBudget: state.tokenBudget,
    setExpandedFolders: state.setExpandedFolders,
    setIsAutoContextEnabled: state.setIsAutoContextEnabled,
    openInPreview: state.openInPreview,
    openPermanently: state.openPermanently,
    setFileTree: state.setFileTree,
    setTreeStringTokenCount: state.setTreeStringTokenCount,
  })));

  const { 
    isFileExplorerSearchVisible: showSearch, 
    setIsFileExplorerSearchVisible: setShowSearch,
    isGitPanelVisible: showGit,
    setIsGitPanelVisible: setShowGit,
  } = useUiStore(useShallow(state => ({
    isFileExplorerSearchVisible: state.isFileExplorerSearchVisible,
    setIsFileExplorerSearchVisible: state.setIsFileExplorerSearchVisible,
    isGitPanelVisible: state.isGitPanelVisible,
    setIsGitPanelVisible: state.setIsGitPanelVisible,
  })));
  const [newFileDialog, setNewFileDialog] = useState<{ isOpen: boolean, basePath: string }>({ isOpen: false, basePath: '' });
  
  const handleCreateNewFile = (node: FileNode) => {
    const basePath = node.type === 'directory' 
      ? (node.path.endsWith('/') ? node.path : `${node.path}/`) 
      : (node.path.substring(0, node.path.lastIndexOf('/') + 1) || '');
    setNewFileDialog({ isOpen: true, basePath });
  };
  
  const handleSaveNewFileFromDialog = async (newPath: string) => {
      try {
        // The path from the dialog will include the base path, so it's a full path
        await http<void>(`/api/files/content`, {
          method: 'POST',
          body: JSON.stringify({
            path: newPath,
            content: '',
          }),
        });
        toast.success(`File "${newPath}" created.`);
        setNewFileDialog({ isOpen: false, basePath: '' });
        onRefresh();
        openPermanently(newPath);
      } catch (error: any) {
        toast.error('Failed to create file.', { description: error.message });
      }
    };


  const [showIgnoredFiles, setShowIgnoredFiles] = useState(false);
  const [showSelectedFilesOnly, setShowSelectedFilesOnly] = useState(false);
  const isMobile = useIsMobile();
  const cachedContents = useFilesStore((state) => state.cachedContents);

  const { handleImproveTests, handleRunTests, handleFixTests } =
    useWorkspaceActions();

  const {
    toggleNodeSelection,
    nodeSelectionStates,
    hasTokenizableDescendants,
    folderSelectionTypes,
    handleSelectAll,
    areAllFilesSelected,
    getAllDescendantFiles,
    combinedSelectedFiles,
  } = useFileSelection();

  useEffect(() => {
    if (combinedSelectedFiles.size === 0 && showSelectedFilesOnly) {
      setShowSelectedFilesOnly(false);
    }
  }, [combinedSelectedFiles, showSelectedFilesOnly]);

  const processedFileTree = useProcessedFileTree(fileTree);

  const filteredFileTree = useMemo(() => {
    if (showSelectedFilesOnly) {
      const allFiles = new Map<string, FileNode>();
      const traverse = (nodes: FileNode[]) =>
        nodes.forEach((n) => {
          allFiles.set(n.path, n);
          if (n.children) traverse(n.children);
        });
      traverse(processedFileTree);
      return Array.from(combinedSelectedFiles)
        .map((path) => allFiles.get(path))
        .filter((n): n is FileNode => !!n);
    }

    if (!showIgnoredFiles) {
      const filterIgnored = (nodes: FileNode[]): FileNode[] => {
        return nodes
          .filter(node => !node.ignored)
          .map(node => {
            if (node.children) {
              return { ...node, children: filterIgnored(node.children) };
            }
            return node;
          });
      };
      return filterIgnored(processedFileTree);
    }

    return processedFileTree;
  }, [showSelectedFilesOnly, processedFileTree, combinedSelectedFiles, showIgnoredFiles]);

  const copyFileToClipboard = React.useCallback(
    async (node: FileNode) => {
      if (node.type !== "file") {
        return;
      }

      if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) {
        toast.error("Clipboard API is not available.");
        return;
      }

      let contentToCopy =
        editedContent[node.path] ?? cachedContents?.[node.path]?.content;
      const toastId = toast.loading(`Copying "${node.name}"...`);

      try {
        if (contentToCopy === undefined) {
          const response = await http<{ content: string }>(
            `/api/files/content?path=${encodeURIComponent(node.path)}`
          );
          contentToCopy = response.content ?? "";
        }

        await navigator.clipboard.writeText(contentToCopy ?? "");
        toast.success("File copied to clipboard.", { id: toastId });
      } catch (error: any) {
        toast.error("Failed to copy file.", {
          id: toastId,
          description: error?.message,
        });
      }
    },
    [cachedContents, editedContent]
  );

  useEffect(() => {
    if (openFilePath) {
      // Use rAF to ensure the DOM is updated after any potential folder expansion,
      // and scroll instantly to provide immediate feedback.
      requestAnimationFrame(() => {
        const element = document.querySelector(
          `[data-path="${CSS.escape(openFilePath)}"]`
        );
        if (element) {
          element.scrollIntoView({ block: "nearest" });
        }
      });
    }
  }, [openFilePath]);

  const toggleFolder = (path: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  const onImproveTests = (node: FileNode) =>
    handleImproveTests(node, getAllDescendantFiles);

  const FileTree = ({
    nodes,
    level = 0,
  }: {
    nodes: FileNode[];
    level?: number;
  }) => (
    <ul
      className="space-y-0"
      style={{ paddingLeft: level > 0 ? "0.5rem" : "0" }}
    >
      {nodes.map((node) => {
        const selectionState =
          node.type === "directory"
            ? nodeSelectionStates.get(node.path) ?? "unchecked"
            : userSelectedFiles.has(node.path) ||
              Array.from(contextSelectedFiles.values()).some((set) =>
                set.has(node.path)
              )
            ? "checked"
            : "unchecked";
        const isExpanded = expandedFolders.has(node.path);
        const canBeSelected =
          node.type === "file"
            ? !!node.tokenizable
            : !!hasTokenizableDescendants.get(node.path) && !node.ignored;
        const hasUnsavedChanges = !!editedContent[node.path];
        const isActiveFile = openFilePath === node.path;

        const isUserSelected = userSelectedFiles.has(node.path);
        const isContextSelected = Array.from(
          contextSelectedFiles.values()
        ).some((set) => set.has(node.path));

        const isContextOnly =
          (node.type === "file" && isContextSelected && !isUserSelected) ||
          (node.type === "directory" &&
            folderSelectionTypes.get(node.path) === "context");

        const desynced = node.coverageDesynced;
        const canRunTests =
          node.type === "file" &&
          !!node.testSummary &&
          node.testSummary.total > 0;
        const canFixTests =
          node.type === "file" &&
          !!node.testSummary &&
          node.testSummary.failing > 0;

        // Dummy handlers for context menu actions as per spec
        const handleDummyAction = (action: string, path: string) => {
          toast.info(`${action} action triggered for ${path}`, {
            description: "This feature can be implemented here.",
          });
        };

        const longPressProps = useLongPress(() => {}, { ms: 300 });

        return (
          <li key={node.path}>
            <FileContextMenu
              onRename={() => handleDummyAction("Rename", node.path)}
              onDelete={() => handleDummyAction("Delete", node.path)}
              onCreateNewFile={() => handleCreateNewFile(node)}
              onCopyFile={
                node.type === "file"
                  ? () => copyFileToClipboard(node)
                  : undefined
              }
              onImproveTests={() => onImproveTests(node)}
              onRunTests={() => handleRunTests(node)}
              onFixTests={() => handleFixTests(node)}
              canRunTests={canRunTests}
              canFixTests={canFixTests}
              canCopyFile={node.type === "file"}
            >
              <div
                {...longPressProps}
                onClick={() => {
                  if (node.type === "directory") {
                    toggleFolder(node.path);
                  } else if (node.type === "file" && node.tokenizable) {
                    openInPreview(node.path);
                  }
                }}
                onDoubleClick={() => {
                  if (node.type === "file" && node.tokenizable) {
                    openPermanently(node.path);
                  }
                }}
                data-path={node.path}
                className={cn(
                  "relative flex justify-between items-center rounded hover:bg-accent/50 px-2 py-0 m-0.5 text-xs cursor-pointer",
                  isContextOnly && "text-purple-600 dark:text-purple-400",
                  node.ignored && "opacity-60",
                  hasUnsavedChanges && "text-yellow-600",
                  isActiveFile && "bg-accent",
                  isMobile && "px-1.5 text-sm",
                )}
              >
                <div
                  className={cn(
                    "flex items-center gap-2 flex-grow min-w-0"
                  )}
                >
                  {canBeSelected ? (
                    <Checkbox
                      id={`cb-${node.path}`}
                      checked={
                        selectionState === "indeterminate"
                          ? "indeterminate"
                          : selectionState === "checked"
                      }
                      onCheckedChange={(checked) => {
                        if (node.type === "directory") {
                          const folderType = folderSelectionTypes.get(node.path);
                          // This is the promotion case for a folder.
                          // If it's fully checked but only with context files, a click
                          // (which is an uncheck) should be treated as a user "check" action.
                          if (folderType === "context" && !checked) {
                            toggleNodeSelection(node, true); // Promote to user selection
                          } else {
                            toggleNodeSelection(node, !!checked);
                          }
                        } else {
                          const isContextButNotUser =
                            isContextSelected && !isUserSelected;
                          // This is the promotion case for an individual file.
                          if (isContextButNotUser && !checked) {
                            toggleNodeSelection(node, true);
                          } else {
                            toggleNodeSelection(node, !!checked);
                          }
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className={cn(
                        "ml-2 h-4 w-4",
                        isContextOnly &&
                          "border-purple-600 data-[state=checked]:bg-purple-600 data-[state=checked]:text-white dark:border-purple-400 dark:data-[state=checked]:bg-purple-400",
                        isMobile && "ml-3 h-5 w-5"
                      )}
                    />
                  ) : (
                    <div
                      className={cn("ml-2 h-4 w-4", isMobile && "ml-3 h-5 w-5")}
                    />
                  )}
                  <div
                    className={cn(
                      "flex items-center gap-2 flex-1 min-w-0 rounded px-2 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                      isMobile && "py-1"
                    )}
                  >
                    <span
                      className={cn(
                        "truncate",
                        isActiveFile && "font-semibold"
                      )}
                    >
                      {node.type === "directory"
                        ? isExpanded
                          ? "📂"
                          : "📁"
                        : "📄"}{" "}
                      {node.name}
                      {hasUnsavedChanges ? "*" : ""}
                    </span>
                  </div>
                </div>
                <div
                  className={cn(
                    "flex items-center shrink-0 gap-2",
                    isMobile && "sticky right-0 z-10 bg-background pl-2 pr-1"
                  )}
                >
                  <TestStatusIndicator testSummary={node.testSummary} />
                  <CoverageIndicator
                    coverage={node.coverage}
                    coverageDesynced={desynced}
                  />
                  <span
                    className={cn(
                      "text-[10px] text-muted-foreground tabular-nums shrink-0 text-right",
                      isMobile ? "w-14" : "w-16"
                    )}
                  >
                    {formatTokenCount(node.tokens)} t
                  </span>
                </div>
              </div>
            </FileContextMenu>
            {node.type === "directory" && isExpanded && node.children && (
              <FileTree nodes={node.children} level={level + 1} />
            )}
          </li>
        );
      })}
    </ul>
  );

  return (
    <>
      <SaveNewFileDialog
        isOpen={newFileDialog.isOpen}
        onClose={() => setNewFileDialog({ isOpen: false, basePath: '' })}
        onSave={handleSaveNewFileFromDialog}
        fileName=""
        language="plaintext"
        basePath={newFileDialog.basePath}
      />
    <ScrollArea className="h-full p-1 border rounded">
      <div className="sticky top-0 z-30 flex justify-between items-center mb-1 pl-1 bg-background pb-1">
        <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onRefresh} title="Refresh File Tree">
                <RefreshCw className="h-4 w-4" />
            </Button>
            <Button variant={showSearch ? 'secondary' : 'ghost'} size="icon" className="h-7 w-7" onClick={() => { setShowSearch(!showSearch); if (!showSearch) setShowGit(false); }} title="Toggle Search">
                <Search className="h-4 w-4" />
            </Button>
            <Button variant={showGit ? 'secondary' : 'ghost'} size="icon" className="h-7 w-7" onClick={() => { setShowGit(!showGit); if (!showGit) setShowSearch(false); }} title="Toggle Git Panel">
                <GitCommit className="h-4 w-4" />
            </Button>
        </div>
                <div className="flex items-center gap-1">

          {!showSearch && !showGit && (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center mr-1">
                    <Switch
                      id="auto-select-context"
                      checked={isAutoContextEnabled}
                      onCheckedChange={setIsAutoContextEnabled}
                      className="data-[state=checked]:bg-primary scale-75"
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Auto-select relevant files on selection</p>
                </TooltipContent>
              </Tooltip>
              <Button
                variant={showIgnoredFiles ? "secondary" : "ghost"}
                size="icon"
                className="h-7 w-7"
                onClick={() => setShowIgnoredFiles(!showIgnoredFiles)}
                title={showIgnoredFiles ? "Hide ignored files" : "Show ignored files"}
              >
                {showIgnoredFiles ? <Eye className="h-4 w-4" /> : <EyeClosed className="h-4 w-4" />}
              </Button>
              {combinedSelectedFiles.size > 0 && (
                <Button variant={showSelectedFilesOnly ? "secondary" : "ghost"} size="icon" className="h-7 w-7" onClick={() => setShowSelectedFilesOnly(!showSelectedFilesOnly)} title="Show selected files">
                  <Files className="h-4 w-4" />
                </Button>
              )}
              <Button variant="outline" size="sm" className="h-7 px-2 py-0 text-xs" onClick={handleSelectAll}>
                {areAllFilesSelected ? "Deselect All" : "Select All"}
              </Button>
            </>
          )}
        </div>
      </div>
      {showSearch 
        ? <WorkspaceSearch /> 
        : showGit ? <GitPanel /> : <FileTree nodes={filteredFileTree} />
      }
      <ScrollBar orientation="horizontal" />
    </ScrollArea>
    </>
  );
};


export const FileExplorer = React.memo(_FileExplorer);